'use client';

import {
    useLayoutEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
    memo,
} from 'react';

import { VerticalImageGallery } from '@petsmart-ui/sparky';
import type { ProductAsset } from '@libs/common-next';
import { DEVICE_TYPE, sanitizeUrl, useDeviceType } from '@libs/common-next';
import type { ProductAssetsGalleryProps } from '@app/pdp/components/ProductCarousel/types';
import type { ImageHoverEvent } from './types';
import { CarouselImage } from './CarouselImage';
import { MagnifierView } from './MagnifierView';
import './ProductAssetsGallery.scss';

export const handleMagnifierHover = (
    e: ImageHoverEvent,
    setMagnifiedImage: (ev: ImageHoverEvent) => void
) => {
    if (typeof e.imagePos !== 'undefined' && typeof e.imgUrl !== 'undefined') {
        setMagnifiedImage({
            imagePos: e.imagePos,
            imgUrl: e.imgUrl,
        });
    }
};

export const handleMagnifierLeave = (
    setMagnifiedImage: (ev: ImageHoverEvent) => void
) => {
    setMagnifiedImage({
        imagePos: [],
        imgUrl: '',
    });
};

function ProductAssetsGallery({
    productAssets,
    wrapperProps,
    itemProps,
    productName,
    openModalAndSetIndex,
    constrainWidth,
    isModalView,
}: ProductAssetsGalleryProps) {
    const [isClient, setIsClient] = useState(false);
    const [magnifiedImage, setMagnifiedImage] = useState<ImageHoverEvent>({
        imagePos: [],
        imgUrl: '',
    });
    const [carouselWidth, setCarouselWidth] = useState(0);
    const [carouselHeight, setCarouselHeight] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);
    const deviceType = useDeviceType();
    const isSmallScreen =
        deviceType === DEVICE_TYPE.MOBILE || deviceType === DEVICE_TYPE.TABLET;
    const tabletPaddingLeft = 82;
    const maxWidth = constrainWidth
        ? carouselWidth -
          (!isSmallScreen ? (!isModalView ? tabletPaddingLeft : 0) : 0)
        : 'none';
    const minHeight = constrainWidth
        ? carouselHeight !== 0
            ? carouselHeight
            : 'none'
        : 'none';

    const setWidthAndHeight = useCallback(() => {
        if (carouselRef.current) {
            setCarouselWidth(carouselRef.current.clientWidth);
            const containerHeight = carouselRef.current.clientHeight;
            const innerImage = carouselRef.current.querySelector('img');
            if (innerImage) {
                const imageHeight = innerImage.clientHeight;
                if (imageHeight !== 0) {
                    setCarouselHeight(imageHeight);
                } else {
                    setCarouselHeight(containerHeight);
                }
            } else {
                setCarouselHeight(containerHeight);
            }
        }
    }, []);

    useLayoutEffect(() => {
        setIsClient(true);
        setWidthAndHeight();

        window.addEventListener('resize', setWidthAndHeight);
        return () => {
            window.removeEventListener('resize', setWidthAndHeight);
        };
    }, [setWidthAndHeight]);

    const verticalImageGalleryItemStyle = useMemo(
        () =>
            !isModalView
                ? isClient
                    ? {
                          minWidth: maxWidth,
                          maxWidth,
                          minHeight,
                      }
                    : {
                          minWidth: '100%',
                          maxWidth: '100%',
                          minHeight: '100%',
                      }
                : undefined,
        [isClient, isModalView, maxWidth, minHeight]
    );

    const isMagnifierEnabled = useMemo(
        () => (isClient && !isSmallScreen ? !isModalView : false),
        [isClient, isSmallScreen, isModalView]
    );

    const magnifierProps = useMemo(
        () =>
            isMagnifierEnabled
                ? {
                      enableMagnifier: true,
                      onHover: (e: ImageHoverEvent) =>
                          handleMagnifierHover(e, setMagnifiedImage),
                      onHoverLeave: () =>
                          handleMagnifierLeave(setMagnifiedImage),
                  }
                : {},
        [isMagnifierEnabled, setMagnifiedImage]
    );

    const handleGalleryImageClick = (index: number) => {
        if (!isClient) {
            return;
        }
        openModalAndSetIndex?.(index);
    };

    const galeryComponent = useMemo(
        () => (
            <VerticalImageGallery
                {...wrapperProps}
                data-testid="product-assets-gallery"
            >
                <>
                    {(productAssets || []).map(
                        (productAsset: ProductAsset, index: number) => {
                            if (productAsset.typeOfAsset === 'image') {
                                const imageUrl = sanitizeUrl(
                                    productAsset.mainUrl
                                ).split('?')[0]; // remove any querry params if exist (get base url)
                                return (
                                    <VerticalImageGallery.Item
                                        {...itemProps}
                                        onClick={() =>
                                            handleGalleryImageClick(index)
                                        }
                                        key={productAsset.mainUrl}
                                        data-testid="product-carousel-image"
                                        style={verticalImageGalleryItemStyle}
                                    >
                                        {/* All props passed to this child component will be picked up by Sparky/VerticalImageGalleryDots
                                             This then throws warning messages in the console. Should be fixed in Sparky.
                                         */}
                                        <CarouselImage
                                            src={`${imageUrl}?fmt=webp&wid=80&hei=80`} // gets picked up by ThumbnailButton from VertialImageGalleryDots. We dont use it in CarouselImage.
                                            mainSrc={imageUrl}
                                            imgAlt={`Product ${productName}`}
                                            priority={
                                                index === 0 ? 'eager' : 'lazy'
                                            } // load first image (LCP element) eagarly
                                            {...magnifierProps}
                                        />
                                    </VerticalImageGallery.Item>
                                );
                            }
                            if (productAsset.typeOfAsset === 'video') {
                                return (
                                    <VerticalImageGallery.Item
                                        key={productAsset.thumbnailUrl}
                                        isVideo
                                        data-testid="product-carousel-video"
                                    >
                                        <VerticalImageGallery.Video
                                            imgSrc={sanitizeUrl(
                                                productAsset.posterImageUrl
                                            )}
                                            videoSrc={sanitizeUrl(
                                                productAsset.videoSizes[0].url // TODO: make videos responsive
                                            )}
                                            className="product-carusel__video"
                                        />
                                    </VerticalImageGallery.Item>
                                );
                            }

                            return null;
                        }
                    )}
                </>
            </VerticalImageGallery>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- we dont want all deps to cause rerenders
        [
            productAssets,
            isClient,
            isSmallScreen,
            isModalView,
            setWidthAndHeight,
            isMagnifierEnabled,
            maxWidth,
            minHeight,
            wrapperProps?.startAtDefault,
        ]
    );

    if (!productAssets?.length) {
        return null;
    }

    return (
        <div ref={carouselRef}>
            {galeryComponent}
            {isMagnifierEnabled ? (
                <MagnifierView
                    imgSrc={magnifiedImage.imgUrl || ''}
                    showMagnifier={!!magnifiedImage.imgUrl}
                    imagePos={magnifiedImage.imagePos || []}
                    carouselWidth={+maxWidth}
                    carouselHeight={+minHeight}
                    positionOffset={tabletPaddingLeft}
                    data-testid="magnifier-view"
                />
            ) : null}
        </div>
    );
}

export default memo(ProductAssetsGallery);
